import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { saasListTeam, saasInviteSeat, saasRemoveSeat, saasMySubscription } from '../api';
import { toast } from '../hooks/use-toast';
import { UserPlus, ArrowLeft, Trash2, Crown, Users } from 'lucide-react';

const AccountingTeam = () => {
  const { lang, dir } = useLang();
  const [seats, setSeats] = useState([]);
  const [sub, setSub] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', role: 'member' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const s = await saasMySubscription(); setSub(s);
      if (s.plan === 'enterprise') {
        const t = await saasListTeam(); setSeats(t.seats || []);
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email) { toast({ title: lang === 'ar' ? 'البريد مطلوب' : 'Email required', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      await saasInviteSeat(form.email, form.role, form.name);
      setForm({ email: '', name: '', role: 'member' });
      toast({ title: lang === 'ar' ? 'تمت الدعوة' : 'Invited' });
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === 'ar' ? 'حذف هذا العضو؟' : 'Remove this seat?')) return;
    try { await saasRemoveSeat(id); load(); toast({ title: lang === 'ar' ? 'حُذف' : 'Removed' }); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  if (sub && sub.plan !== 'enterprise') {
    return (
      <div className="pt-24 pb-16 min-h-screen bg-[#f8fafc]" dir={dir}>
        <div className="max-w-3xl mx-auto px-6 lg:px-10 mt-8 glass-card rounded-2xl p-10 text-center">
          <Crown className="w-10 h-10 text-[#2563eb] mx-auto mb-4" />
          <h1 className="font-display text-2xl text-[#0a0a0a] mb-2">{lang === 'ar' ? 'ميزة Enterprise' : 'Enterprise Feature'}</h1>
          <p className="text-sm text-[#111827]/60 mb-6">{lang === 'ar' ? 'إدارة الفريق ومقاعد المحاسبين متاحة في باقة Enterprise فقط.' : 'Team seats are available on the Enterprise plan only.'}</p>
          <Link to="/accounting/pricing"><Button className="gold-button rounded-md h-11 px-6 text-sm">{lang === 'ar' ? 'ترقية' : 'Upgrade'}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#f8fafc]" dir={dir} data-testid="team-page">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <Link to="/accounting" className="inline-flex items-center gap-2 text-xs text-[#2563eb] hover:underline mb-4"><ArrowLeft className="w-3 h-3" /> {lang === 'ar' ? 'العودة للوحة' : 'Back to Dashboard'}</Link>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#2563eb] text-white flex items-center justify-center"><Users className="w-6 h-6" /></div>
          <div>
            <h1 className="font-display text-3xl text-[#0a0a0a]">{lang === 'ar' ? 'الفريق' : 'Team & Seats'}</h1>
            <p className="text-sm text-[#111827]/60">{lang === 'ar' ? 'ادعُ محاسبين ومساعدين للتعاون على دفاترك' : 'Invite accountants and assistants to collaborate on your books'}</p>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6 mb-6">
          <div className="text-xs uppercase tracking-widest text-[#2563eb] mb-3">{lang === 'ar' ? 'دعوة عضو جديد' : 'Invite New Member'}</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@company.com" className="h-10 bg-white border-[#e5e7eb]" data-testid="invite-email" />
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={lang === 'ar' ? 'الاسم' : 'Name'} className="h-10 bg-white border-[#e5e7eb]" data-testid="invite-name" />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-10 bg-white border-[#e5e7eb]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="member">{lang === 'ar' ? 'عضو (قراءة/تعديل)' : 'Member (view + edit)'}</SelectItem>
                <SelectItem value="admin">{lang === 'ar' ? 'مدير (كل الصلاحيات)' : 'Admin (all permissions)'}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={invite} disabled={busy} className="gold-button rounded-md h-10 text-xs" data-testid="invite-submit">
              <UserPlus className="w-4 h-4 me-2" /> {lang === 'ar' ? 'دعوة' : 'Send Invite'}
            </Button>
          </div>
          <div className="mt-3 text-[10px] text-[#111827]/50">{lang === 'ar' ? 'سيتلقى العضو دعوة تسجيل الدخول عبر البريد. الأدوار قابلة للتعديل لاحقاً.' : 'The member will receive an invitation email. Roles are editable later.'}</div>
        </div>

        <div className="glass-card rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-[#f8fafc]">
              <tr>
                <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الاسم' : 'Name'}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'البريد' : 'Email'}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الدور' : 'Role'}</th>
                <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الحالة' : 'Status'}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {seats.length === 0 ? (
                <tr><td colSpan="5" className="p-10 text-center text-[#111827]/40 text-xs">{lang === 'ar' ? 'لا أعضاء بعد — ابدأ بدعوة زميلك المحاسب.' : 'No members yet — invite your accountant to get started.'}</td></tr>
              ) : seats.map((s) => (
                <tr key={s.id} className="border-t border-[#e5e7eb]" data-testid={`seat-${s.id}`}>
                  <td className="p-3 text-[#0a0a0a]">{s.name || '—'}</td>
                  <td className="p-3 text-[#111827]/70">{s.email}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${s.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{s.role}</span>
                  </td>
                  <td className="p-3 text-[#111827]/60 capitalize">{s.status}</td>
                  <td className="p-3 text-end">
                    <button onClick={() => remove(s.id)} className="text-rose-500 hover:text-rose-600" data-testid={`remove-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountingTeam;
